import { Group, Label } from '@tlx/atlas';
import React, { useState } from 'react';
import { ValidatedFormInput } from '@Page/Onboarding/components/ValidatedForm/ValidatedInput';
import { useRangeConstraintValidator } from '@Page/Onboarding/validators/rangeConstraintValidator';
import { useIntegerValidator } from '@Page/Onboarding/validators/integerValidator';
import { APIError, ValidationMessage } from '@tlx/astro-shared';
import { OnboardingStageSkeleton } from '@Page/Onboarding/components/OnboardingStageSkeleton';
import {
    SalarySettingsHolidayDTO,
    updateHolidaySettings,
    useLatestHoliday,
} from '@Page/Onboarding/hooks/useLatestHoliday';
import { setCustomValidityOnField } from '@Page/Onboarding/components/ValidatedForm/setCustomValidityOnField';
import { FormattedMessage, useIntl } from 'react-intl';
import { JumboRadio, useSharedTracking } from '@tlx/beehive-shared';
import { JumboRadioGroup } from '@Page/Onboarding/JumboRadioGroup';
import { createOrdinalFromArray } from '@Util/misc';
import { SalaryOnboardingStageForm } from '../SalaryOnboardingStage';

const DEFAULT_VALUES_5_WEEKS = {
    vacationDays: '25',
    vacationPayRate: '12.0',
    vacationPayRateAbove59: '14.3',
};

const DEFAULT_VALUES_4_WEEKS = {
    vacationDays: '21',
    vacationPayRate: '10.2',
    vacationPayRateAbove59: '12.5',
};

const DEFAULT_VALUES_CUSTOM = {
    vacationDays: undefined,
    vacationPayRate: undefined,
    vacationPayRateAbove59: undefined,
};

const DEFAULT_VALUES = {
    '5': DEFAULT_VALUES_5_WEEKS,
    '4': DEFAULT_VALUES_4_WEEKS,
    custom: DEFAULT_VALUES_CUSTOM,
} as const;

type VacationPayType = keyof typeof DEFAULT_VALUES;

type VacationPayAndRateStageFormValues = {
    vacationDays: string;
    vacationPayRate: string;
    vacationPayRateAbove59: string;
};

function mapToHolidayFormValues(dto: SalarySettingsHolidayDTO): VacationPayAndRateStageFormValues {
    return {
        vacationDays: String(dto.days),
        vacationPayRate: String(dto.vacationPayPercentage1),
        vacationPayRateAbove59: String(dto.vacationPayPercentage2),
    };
}

function mapToHolidayDTO(values: Partial<VacationPayAndRateStageFormValues>): Partial<SalarySettingsHolidayDTO> {
    return {
        days: values.vacationDays ? Number(values.vacationDays) : undefined,
        vacationPayPercentage1: values.vacationPayRate ? Number(values.vacationPayRate.replace(',', '.')) : undefined,
        vacationPayPercentage2: values.vacationPayRateAbove59
            ? Number(values.vacationPayRateAbove59.replace(',', '.'))
            : undefined,
    };
}

export function ConnectedVacationPayAndRateStage() {
    const { data, isLoading } = useLatestHoliday();
    if (!data || isLoading) {
        return <OnboardingStageSkeleton />;
    }
    const id = data.id;
    const defaultValue = mapToHolidayFormValues(data);

    return (
        <VacationPayAndRateStage
            defaultValue={defaultValue}
            handleSubmit={async (formData) => {
                const dto = mapToHolidayDTO(formData);
                await updateHolidaySettings(id, dto);
            }}
            handleError={(form, error) => {
                const unhandled: ValidationMessage[] = [];
                error.details.validationMessages?.forEach((message) => {
                    if (message.field) {
                        const handled = setCustomValidityOnField(form, message.field, message.message);

                        if (!handled) {
                            unhandled.push(message);
                        }
                    }
                });
                return unhandled;
            }}
        />
    );
}

type VacationPayAndRateStageProps = {
    defaultValue: VacationPayAndRateStageFormValues;
    handleSubmit?: (formData: Partial<VacationPayAndRateStageFormValues>) => Promise<void>;
    handleError?: (form: HTMLFormElement, error: APIError) => ValidationMessage[] | void;
};

// TODO: idk I feel like there must be some easier way to do this kind of ting :upside_down_face:
function getVacationPayTypeFromFormValues(values: VacationPayAndRateStageFormValues) {
    if (
        Number(values.vacationDays) === Number(DEFAULT_VALUES_5_WEEKS.vacationDays) &&
        Number(values.vacationPayRate) === Number(DEFAULT_VALUES_5_WEEKS.vacationPayRate) &&
        Number(values.vacationPayRateAbove59) === Number(DEFAULT_VALUES_5_WEEKS.vacationPayRateAbove59)
    ) {
        return '5';
    } else if (
        Number(values.vacationDays) === Number(DEFAULT_VALUES_4_WEEKS.vacationDays) &&
        Number(values.vacationPayRate) === Number(DEFAULT_VALUES_4_WEEKS.vacationPayRate) &&
        Number(values.vacationPayRateAbove59) === Number(DEFAULT_VALUES_4_WEEKS.vacationPayRateAbove59)
    ) {
        return '4';
    } else {
        return 'custom';
    }
}

export function VacationPayAndRateStage({ defaultValue, handleError, handleSubmit }: VacationPayAndRateStageProps) {
    const intl = useIntl();
    const integerValidator = useIntegerValidator();
    const rangeConstraintValidator = useRangeConstraintValidator();
    const [vacationPayType, setVacationPayType] = useState<VacationPayType>(() =>
        getVacationPayTypeFromFormValues(defaultValue),
    );
    const defaultValues = vacationPayType === 'custom' ? defaultValue : DEFAULT_VALUES[vacationPayType];

    const above59Ref = React.useRef<HTMLInputElement>(null);

    return (
        <SalaryOnboardingStageForm
            title={intl.formatMessage({ id: 'tsk_onboarding_vacation_step' })}
            description={intl.formatMessage({
                id: 'tsk_onboarding_vacation_description',
            })}
            handleSubmit={handleSubmit}
            handleError={handleError}
        >
            <JumboRadioGroup name="vacationPayType">
                <JumboRadio
                    onClick={(e) => {
                        setVacationPayType('5');
                        e.currentTarget.form?.reset();
                    }}
                    checked={vacationPayType === '5'}
                    value="5"
                    data-testid="vacationPayType-5-radio"
                >
                    <div>
                        <div className="atl-text-xl atl-font-medium">
                            <FormattedMessage
                                id={'tsk_onboarding_vacation_x_week_label'}
                                values={createOrdinalFromArray([5])}
                            />
                        </div>
                        <div>
                            <FormattedMessage
                                id="tsk_onboarding_vacation_x_week_sublabel"
                                values={createOrdinalFromArray(['12 %'])}
                            />
                        </div>
                    </div>
                </JumboRadio>
                <JumboRadio
                    onClick={(e) => {
                        setVacationPayType('4');
                        e.currentTarget.form?.reset();
                    }}
                    checked={vacationPayType === '4'}
                    value="4"
                    data-testid="vacationPayType-4-radio"
                >
                    <div>
                        <div className="atl-text-xl atl-font-medium">
                            <FormattedMessage
                                id={'tsk_onboarding_vacation_x_week_label'}
                                values={createOrdinalFromArray([4])}
                            />
                        </div>
                        <div>
                            <FormattedMessage
                                id={'tsk_onboarding_vacation_x_week_sublabel'}
                                values={createOrdinalFromArray(['10,2 %'])}
                            />
                        </div>
                    </div>
                </JumboRadio>
                <JumboRadio
                    onClick={() => {
                        setVacationPayType('custom');
                    }}
                    checked={vacationPayType === 'custom'}
                    value="custom"
                    data-testid="vacationPayType-custom-radio"
                >
                    <div>
                        <div style={{ marginBlock: 10 }} className="atl-text-xl atl-font-medium">
                            <FormattedMessage id={'text_custom'} />
                        </div>
                    </div>
                </JumboRadio>
            </JumboRadioGroup>

            <div className="atl-flex atl-gap-8">
                <Group className="atl-w-full">
                    <Label htmlFor="vacationDays">
                        <FormattedMessage id={'text_days'} />
                    </Label>
                    <ValidatedFormInput
                        className="atl-w-full"
                        id="vacationDays"
                        name="vacationDays"
                        data-testid={useSharedTracking('vacation-days')}
                        defaultValue={defaultValues.vacationDays}
                        readOnly={vacationPayType !== 'custom'}
                        required
                        type="text"
                        inputMode="numeric"
                        validator={(target) => integerValidator(target.value)}
                    />
                </Group>
            </div>
            <div className="atl-flex atl-gap-8">
                <Group className="atl-w-full">
                    <Label htmlFor="vacationPayRate">
                        <FormattedMessage id={'text_vacation_allowance_percentage'} />
                    </Label>
                    <ValidatedFormInput
                        className="atl-w-full"
                        id="vacationPayRate"
                        name="vacationPayRate"
                        data-testid={useSharedTracking('vacation-pay-rate')}
                        defaultValue={defaultValues.vacationPayRate}
                        readOnly={vacationPayType !== 'custom'}
                        required
                        type="text"
                        inputMode="decimal"
                        validator={(target) =>
                            rangeConstraintValidator(target.value.replace(',', '.'), {
                                min: 0,
                                max: 100,
                            })
                        }
                        onBlur={(e) => {
                            const value = Number(e.target.value);
                            if (value > 0 && value <= 100 && above59Ref.current) {
                                above59Ref.current.value = String(Math.min(value + 2.3, 100));
                            }
                        }}
                    />
                </Group>
                <Group className="atl-w-full">
                    <Label htmlFor="vacationPayRateAbove59">
                        <FormattedMessage id={'text_provision_salary_rate_vac_allow_60_label'} />
                    </Label>
                    <ValidatedFormInput
                        ref={above59Ref}
                        className="atl-w-full"
                        id="vacationPayRateAbove59"
                        name="vacationPayRateAbove59"
                        data-testid={useSharedTracking('vacation-pay-rate-above-59')}
                        defaultValue={defaultValues.vacationPayRateAbove59}
                        readOnly={vacationPayType !== 'custom'}
                        required
                        type="text"
                        inputMode="decimal"
                        validator={(target) =>
                            rangeConstraintValidator(target.value.replace(',', '.'), {
                                min: 0,
                                max: 100,
                            })
                        }
                    />
                </Group>
            </div>
        </SalaryOnboardingStageForm>
    );
}
